// Geld- & Steuer-Logik — die EINE Wahrheit für MwSt/Netto/Brutto in WerkstattFlow.
// Ersetzt die im Altcode 3× kopierte 19%-Rechnung (Audit-Fund "MwSt 3× kopiert"):
// genau ein Modul, von Detail-Ansicht, Rechnungsdruck und Kundenfreigabe gemeinsam genutzt.
//
// Konvention: Geld IMMER in ganzen Cent (integer) rechnen, nie in Float-Euro — vermeidet
// Rundungsdrift. Formatierung nach Euro nur an der Anzeige-Grenze.

// USt-Sätze (19/7/0) leben in `inventory.ts` (dort zuerst gebraucht). Hier nur wiederverwendet — EINE Wahrheit.
// Reihenfolge ABSTEIGEND (19, 7, 0) → `vatTotals` liefert die Steuergruppen gleich in Anzeige-Reihenfolge.
// (Die Altteilsteuer ist bewusst NICHT abgebildet — bei Bedarf später als eigene Steuer-Klasse ergänzbar.)
import { VAT_RATES, type VatRate } from './inventory'

/** Deutscher Regel-Umsatzsteuersatz in Prozent. Als ganze Zahl, um Float-Drift zu vermeiden. */
export const VAT_RATE_PERCENT = 19

/** Klemmt eine beliebige Eingabe auf einen gültigen Satz; Unbekanntes/fehlend → 19 (sicherer Default). */
export function cleanVatRate(rate: number | null | undefined): VatRate {
  return rate === 7 ? 7 : rate === 0 ? 0 : 19
}

/** Kürzel für die Steuer-Spalte der Rechnung: 19→'V' (voll), 7→'E' (ermäßigt), sonst 'o' (ohne). */
export function taxLabel(rate: number): 'V' | 'E' | 'o' {
  return rate === 7 ? 'E' : rate === 0 ? 'o' : 'V'
}

/** Eine zu versteuernde Position: Netto JE STÜCK in Cent, Menge, USt-Satz. */
export interface VatLine {
  netCents: number
  qty: number
  vatRate: number
}
/** USt-Gruppe eines Satzes: Bemessungsgrundlage (Netto-Summe) + darauf entfallende Steuer. */
export interface VatRateGroup {
  rate: VatRate
  netCents: number
  vatCents: number
}
export interface VatTotals {
  /** Netto-Gesamtbetrag (alle Sätze, inkl. 0 %). */
  netCents: number
  /** USt-Gesamtbetrag (Σ aller Gruppen). */
  vatCents: number
  /** Brutto = Netto + USt. */
  grossCents: number
  /** Steuer-Aufschlüsselung je vorkommendem Satz > 0 % (absteigend). Leer bei Kleinunternehmer. */
  byRate: VatRateGroup[]
}

/**
 * Summiert Positionen zu Netto/USt/Brutto MIT Steuer-Aufschlüsselung je Satz (für die §14-Rechnung,
 * die USt nach Sätzen getrennt ausweisen muss). Die USt wird BEWUSST je Satz-Gruppe auf die Netto-Summe
 * gerundet (nicht pro Zeile) — verhindert Cent-Drift gegenüber der Summenrechnung der Anzeige.
 * 0 %-Zeilen zählen ins Netto, erzeugen aber keine Steuergruppe. Kleinunternehmer (§19) → keine USt, byRate=[].
 */
export function vatTotals(lines: readonly VatLine[], smallBusiness = false): VatTotals {
  const netByRate = new Map<VatRate, number>()
  let netTotal = 0
  for (const line of lines) {
    const rate = cleanVatRate(line.vatRate)
    const unit = Math.round(line.netCents) || 0
    const qty = Number.isFinite(line.qty) ? Math.max(0, Math.floor(line.qty)) : 0
    const lineNet = unit * qty
    netTotal += lineNet
    netByRate.set(rate, (netByRate.get(rate) ?? 0) + lineNet)
  }
  if (smallBusiness) {
    return { netCents: netTotal, vatCents: 0, grossCents: netTotal, byRate: [] }
  }
  const byRate: VatRateGroup[] = []
  let vatTotal = 0
  for (const rate of VAT_RATES) {
    if (rate === 0) continue // 0 % erzeugt keine Steuergruppe (fließt nur ins Netto)
    const groupNet = netByRate.get(rate) ?? 0
    if (groupNet === 0) continue
    const vatCents = Math.round((groupNet * rate) / 100)
    vatTotal += vatCents
    byRate.push({ rate, netCents: groupNet, vatCents })
  }
  return { netCents: netTotal, vatCents: vatTotal, grossCents: netTotal + vatTotal, byRate }
}

export interface VatBreakdown {
  /** Nettobetrag in Cent (Eingabe, auf ganze Cent gerundet). */
  netCents: number
  /** Umsatzsteuer in Cent. 0 bei Kleinunternehmer (§19 UStG). */
  vatCents: number
  /** Bruttobetrag in Cent (= netto + USt). */
  grossCents: number
}

/**
 * Zerlegt einen Nettobetrag in Umsatzsteuer + Brutto.
 *
 * @param netCents     Nettobetrag in ganzen Cent.
 * @param smallBusiness true = Kleinunternehmer nach §19 UStG → keine USt ausgewiesen (brutto = netto).
 */
export function vatBreakdown(netCents: number, smallBusiness = false): VatBreakdown {
  const net = Math.round(netCents) || 0
  const vatCents = smallBusiness ? 0 : Math.round((net * VAT_RATE_PERCENT) / 100)
  return { netCents: net, vatCents, grossCents: net + vatCents }
}

// Ein wiederverwendeter Intl-Formatter (korrekte deutsche Währungsdarstellung inkl. Tausendertrenner).
const EUR_FORMAT = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })

/** Formatiert Cent als deutschen Euro-String, z. B. 12345 → "123,45 €". */
export function formatEur(cents: number): string {
  return EUR_FORMAT.format((cents || 0) / 100)
}

/**
 * Parst eine Preis-Eingabe robust nach Cent. Akzeptiert deutsche ("12,50") und
 * englische ("12.50") Notation sowie Leerraum. Ungültig/leer → 0.
 * Negativ wird auf 0 geklemmt: werkstatt-tauglich (North Star #1) — eine Fehleingabe
 * darf niemals eine negative Rechnung erzeugen. Die API-Schicht entscheidet, ob sie
 * eine geklemmte Eingabe zusätzlich als Hinweis zurückmeldet (Audit-Fund #7: kein stiller Erfolg).
 */
export function parsePriceCents(input: string | number): number {
  if (typeof input === 'number') {
    return Number.isFinite(input) ? Math.max(0, Math.round(input * 100)) : 0
  }
  const value = parseFloat(String(input).trim().replace(',', '.'))
  return Number.isFinite(value) ? Math.max(0, Math.round(value * 100)) : 0
}

/** Parst eine Mengen-Eingabe; Mindestmenge 1 (nie 0 oder negativ). */
export function parseQty(input: string | number): number {
  const value = typeof input === 'number' ? input : parseInt(String(input), 10)
  return Number.isFinite(value) && value >= 1 ? Math.floor(value) : 1
}
